import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AdminUser } from "../../services/types/adminUser.type";

export const adminUsersApi = createApi({
  reducerPath: "adminUsersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_ADMIN_API_URL, // e.g. "https://api.miyukon.store/admin"
    credentials: "include",
  }),
  tagTypes: ["AdminUser"],
  endpoints: (builder) => ({
    // Список пользователей
    getUsers: builder.query<
      { items: AdminUser[]; total: number; page: number; limit: number },
      { page: number; limit: number }
    >({
      query: ({ page, limit }) => ({
        url: `/users?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      transformResponse: (response: {
        status: string;
        data: {
          items: AdminUser[];
          total: number;
          page: number;
          limit: number;
        };
      }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ uuid }) => ({
                type: "AdminUser" as const,
                id: uuid,
              })),
              { type: "AdminUser", id: "LIST" },
            ]
          : [{ type: "AdminUser", id: "LIST" }],
    }),

    // Детали одного пользователя
    getUserById: builder.query<AdminUser, string>({
      query: (id) => ({ url: `/users/${id}`, method: "GET" }),
      transformResponse: (response: { status: string; data: AdminUser }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "AdminUser", id }],
    }),

    // Обновить профиль пользователя
    updateUser: builder.mutation<
      AdminUser,
      { id: string; body: Partial<AdminUser> }
    >({
      query: ({ id, body }) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: { status: string; data: AdminUser }) =>
        response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminUser", id },
        { type: "AdminUser", id: "LIST" },
      ],
    }),

    // Сгенерировать токен сброса пароля
    resetPassword: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/users/${id}/reset-password`,
        method: "POST",
      }),
      transformResponse: (response: { status: string; message: string }) => ({
        message: response.message,
      }),
      invalidatesTags: [{ type: "AdminUser", id: "LIST" }],
    }),

    // Заблокировать пользователя
    blockUser: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/users/${id}/block`,
        method: "POST",
      }),
      transformResponse: (response: { status: string; message: string }) => ({
        message: response.message,
      }),
      invalidatesTags: (result, error, id) => [{ type: "AdminUser", id }],
    }),

    // Разблокировать пользователя
    unblockUser: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/users/${id}/unblock`,
        method: "POST",
      }),
      transformResponse: (response: { status: string; message: string }) => ({
        message: response.message,
      }),
      invalidatesTags: (result, error, id) => [{ type: "AdminUser", id }],
    }),

    // Удалить пользователя (soft)
    deleteUser: builder.mutation<{ uuid: string }, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      transformResponse: (_: any, _error, id) => ({ uuid: id }),
      invalidatesTags: [{ type: "AdminUser", id: "LIST" }],
    }),

    // Удалить пользователя (hard)
    hardDeleteUser: builder.mutation<{ uuid: string }, string>({
      query: (email) => ({
        url: `/users/dev/user?email=${email}`,
        method: "DELETE",
      }),
      transformResponse: (_: any, _error, id) => ({ uuid: id }),
      invalidatesTags: [{ type: "AdminUser", id: "LIST" }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useResetPasswordMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useDeleteUserMutation,
  useHardDeleteUserMutation,
} = adminUsersApi;
